import React, { useMemo } from 'react'

import { sortBy } from 'lib/array/arrayUtils'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import OfferListCarousel from 'components/OfferList/OfferListCarousel'
import { MaldivesDirectBundleProvider } from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectBundleContext'
import Group from 'components/utils/Group'
import { FlightsClassTypes } from 'constants/flight'
import { CHARTER_PAGE_SLUG } from 'constants/charterFlight'
import useMaldivesCharterOfferList from 'hooks/MaldivesBundle/useMaldivesCharterOfferList'
import useOffers from 'hooks/Offers/useOffers'
import TextButton from 'LuxKit/Button/TextButton'
import Heading from 'LuxKit/Typography/Heading'
import { rem } from 'polished'
import styled from 'styled-components'
import OfferCarouselLookingForSomethingElse from '../LookingForSomethingElse/OfferCarouselLookingForSomethingElse'

const CarouselContainer = styled(Pane)`
  padding-top: ${rem(40)};
  padding-bottom: ${rem(40)};
`

interface Props {
  titleVariant?: React.ComponentProps<typeof Heading>['variant']
  overrideTitle?: React.ReactNode
  overrideOfferList?: App.OfferList
  excludedIds?: Array<string>
}

/**
 * Displays Maldives charter flight offers
 */
function MaldivesCharterCarousel({
  titleVariant = 'heading2',
  overrideTitle,
  overrideOfferList,
  excludedIds,
}: Props) {
  const maldivesOfferList = useMaldivesCharterOfferList({ excludedIds })
  const title = overrideTitle ?? <>Australia's <i>only</i> non-stop flights to the Maldives</>

  // Link to Maldives Direct charter page
  const searchUrl = `/campaign/${CHARTER_PAGE_SLUG}`

  // Bundle context for displaying flight + hotel prices
  const bundleContextValue = useMemo(() => ({
    charterBundle: true,
    isPricePerPerson: true,
    fareClass: FlightsClassTypes.ECONOMY,
  }), [])

  // Fetch full offer objects for price sorting
  const [offers, offersFetching] = useOffers<App.HotelOffer>(maldivesOfferList.offerIds)

  const allOffersLoaded = !maldivesOfferList.fetching &&
    !offersFetching &&
    offers.length > 0 &&
    offers.length === maldivesOfferList.offerIds.length

  // Sort offers by price (cheapest first) - client-side sorting since light endpoint doesn't support sortBy
  const sortedOfferList = useMemo<App.OfferList>(() => {
    if (!allOffersLoaded) {
      return maldivesOfferList
    }

    const priceById = new Map(
      offers.map(offer => [offer.id, offer.lowestPricePackage?.price ?? Number.MAX_VALUE]),
    )

    const sortedOfferIds = sortBy(
      maldivesOfferList.offerIds,
      id => priceById.get(id) ?? Number.MAX_VALUE,
      'asc',
    )

    return { ...maldivesOfferList, offerIds: sortedOfferIds }
  }, [maldivesOfferList, allOffersLoaded, offers])

  // Don't render if no offers available
  if (!maldivesOfferList.fetching && maldivesOfferList.offerIds.length === 0) {
    return null
  }

  return (
    <CarouselContainer>
      <VerticalSpacer gap={24}>
        <LayoutContainer>
          <VerticalSpacer gap={16}>
            <Group
              direction="horizontal"
              horizontalAlign="space-between"
              verticalAlign="center"
              gap={16}
            >
              <Group direction="horizontal" gap={16} verticalAlign="center">
                <Heading variant={titleVariant}>{title}</Heading>
              </Group>
              <Group direction="horizontal" gap={16} verticalAlign="center">
                <TextButton
                  kind="tertiary"
                  to={searchUrl}
                >
                  View all
                </TextButton>
              </Group>
            </Group>
          </VerticalSpacer>
        </LayoutContainer>
        <MaldivesDirectBundleProvider value={bundleContextValue}>
          <OfferListCarousel
            overrideOfferList={overrideOfferList ?? sortedOfferList}
            selectedDatesAndRoomsFilter={maldivesOfferList.bundleFilters}
            tabletLimit="default"
            tileStyle="card-default"
            tileSize="medium"
          >
            <OfferCarouselLookingForSomethingElse to={searchUrl} />
          </OfferListCarousel>
        </MaldivesDirectBundleProvider>
      </VerticalSpacer>
    </CarouselContainer>
  )
}
export default MaldivesCharterCarousel
